// [phase11-v1] Signal pending panels — trading-ui.
//
// Explicit "Pending future phase" placeholders. Per the Phase 11A spec and
// Phase 11D work order, the Signal MUST NOT fabricate ML / probability /
// accuracy / P&L fields. The dashboard mockup ZIP listed several such
// fields that the backend does not produce — surface them as honest
// placeholders instead.
import { BarChart3, Brain, Layers, ShieldAlert } from "lucide-react";

function PanelCard({
  icon,
  title,
  phaseLabel,
  body,
  badgeTone,
}: {
  icon: React.ReactNode;
  title: string;
  phaseLabel: string;
  body: string;
  badgeTone: "amber" | "blue" | "green";
}) {
  const badge =
    badgeTone === "green"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : badgeTone === "blue"
      ? "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30"
      : "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30";
  return (
    <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4 opacity-95">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          {icon} {title}
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border ${badge}`}
        >
          {phaseLabel}
        </span>
      </div>
      <div className="text-xs text-[var(--muted-text)] leading-relaxed">{body}</div>
    </div>
  );
}

export default function SignalPendingPanel({
  labelsAvailable = false,
}: {
  labelsAvailable?: boolean;
}) {
  // Phase 11B — the Outcome Labeling card flips from "pending" to "shipped"
  // when the labels endpoint returned rows. Hit-rate/drift/accuracy remain
  // future-phase work.
  const outcomeCardBody = labelsAvailable
    ? "Deterministic outcome labels are LIVE — per-symbol 09:30–10:30 ET classifications are shown inside each candidate row. Hit-rate, win-rate, and drift are still future-phase work (no ML, no fabricated probabilities)."
    : "Hit-rate, win-rate, simulated P&L, drift state, burn-in. The label engine schema landed in Phase 10 (no values yet). Phase 11B populates the first forward-return labels at 11:00 ET daily.";
  const outcomeCardLabel = labelsAvailable ? "Phase 11B — Labels Live" : "Pending Phase 11 labels";
  const outcomeCardTone: "amber" | "green" = labelsAvailable ? "green" : "amber";

  return (
    <section data-testid="signal-pending-panel" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <PanelCard
        icon={<ShieldAlert className="h-4 w-4 text-amber-500" />}
        title="Risk Veto Signal"
        phaseLabel="Pending Phase 11+"
        badgeTone="amber"
        body="Halt / dilution / spread / liquidity vetos with per-symbol enums. Phase 9 surfaces deterministic passed/failed filters per candidate — Council veto layering is not built yet."
      />
      <PanelCard
        icon={<Layers className="h-4 w-4 text-blue-500" />}
        title="Regime Context"
        phaseLabel="Pending Phase 11+"
        badgeTone="amber"
        body="Broad market trend, market breadth, theme clustering, and macro event-day context are not fully surfaced yet. The current release only shows the early volatility proxy."
      />
      <PanelCard
        icon={<BarChart3 className="h-4 w-4 text-emerald-500" />}
        title="Outcome Labeling · Drift · Accuracy"
        phaseLabel={outcomeCardLabel}
        badgeTone={outcomeCardTone}
        body={outcomeCardBody}
      />
      <PanelCard
        icon={<Brain className="h-4 w-4 text-emerald-500" />}
        title="Council Debate · Final Action"
        phaseLabel="Future phase"
        badgeTone="amber"
        body="Brain / Owl / Elephant / Compass / Risk consensus and decision routing. Not wired to The Signal yet."
      />
      <PanelCard
        icon={<Layers className="h-4 w-4 text-blue-500" />}
        title="Catalyst Recency"
        phaseLabel="Phase 10 informational"
        badgeTone="blue"
        body="The Phase 10 decoder writes per-symbol catalyst_signals.json (recency bands 1.0 / 0.9 / 0.6 / 0.5 / 0.3 / 0.2 / 0.0). The signal is computed but weighted 0.0 in the Phase 9 ranking — operator decides when to enable. Sentiment classification is intentionally out of scope."
      />
      <PanelCard
        icon={<BarChart3 className="h-4 w-4 text-emerald-500" />}
        title="Ranking Method (Phase 9)"
        phaseLabel="Deterministic"
        badgeTone="green"
        body="Composite ∈ [0, 1] from six bounded components (volume_quality, spread_quality, float_quality, price_quality, vix_regime, catalyst_recency). Weights: 0.30 / 0.25 / 0.15 / 0.10 / 0.20 / 0.00. No ML. No hidden model. Hand-auditable per candidate via score_components."
      />
    </section>
  );
}
